import { HttpError } from '@/api/common';
import { feedbackConfigurationApi } from '@/api/feedback/feedbackConfigurationApi';
import { ScheduledReportFrequency, type AlertConfiguration, type ReportConfiguration } from '@/models/feedback/Configuration';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import ReportDialog from './dialogs/ReportDialog';

const Configuration = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.feedback);
    const canUpdate = perms.canUpdate(Perm.feedback);
    const canDelete = perms.canDelete(Perm.feedback);

    // Scope comes from the global top-bar selector.
    const brandId = app.brandId ?? undefined;
    const locationId = app.locationId ?? undefined;

    const [tab, setTab] = useState(0);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [editingReport, setEditingReport] = useState<ReportConfiguration | null>(null);

    const [emails, setEmails] = useState('');
    const [phones, setPhones] = useState('');

    const frequencyLabel = (f: ScheduledReportFrequency) => {
        switch (f) {
            case ScheduledReportFrequency.Daily: return strings.feedbackFrequencyDaily;
            case ScheduledReportFrequency.Weekly: return strings.feedbackFrequencyWeekly;
            case ScheduledReportFrequency.Monthly: return strings.feedbackFrequencyMonthly;
            default: return strings.feedbackFrequencyNone;
        }
    };

    const { data: alert } = useQuery<AlertConfiguration | null>({
        queryKey: ['feedback-alert', locationId ?? ''],
        enabled: !!locationId,
        staleTime: 0,
        queryFn: async () => {
            try {
                return await feedbackConfigurationApi.getAlert(locationId!);
            } catch (e) {
                if (e instanceof HttpError && e.status === 404) return null;
                throw e;
            }
        },
    });

    useEffect(() => {
        setEmails(alert?.triggerEmailAddresses ?? '');
        setPhones(alert?.triggerPhoneNumbers ?? '');
    }, [alert, locationId]);

    const { data: reports, isPending: isReportsLoading } = useQuery<ReportConfiguration[]>({
        queryKey: ['feedback-reports', brandId ?? '', locationId ?? ''],
        enabled: !!brandId,
        staleTime: 0,
        queryFn: () => feedbackConfigurationApi.listReports(brandId, locationId),
    });

    const { mutateAsync: saveAlert, isPending: isSavingAlert } = useMutation({
        mutationFn: () => feedbackConfigurationApi.upsertAlert(locationId!, { triggerEmailAddresses: emails || null, triggerPhoneNumbers: phones || null }),
        onSuccess: async () => { toast({ severity: 'success', message: strings.feedbackAlertSaved }); await qc.invalidateQueries({ queryKey: ['feedback-alert', locationId ?? ''] }); },
        onError: () => toast({ severity: 'error', message: strings.feedbackAlertSaveError }),
    });

    const { mutateAsync: deleteReport } = useMutation({
        mutationFn: (id: string) => feedbackConfigurationApi.deleteReport(id),
        onSuccess: async () => { toast({ severity: 'success', message: strings.feedbackReportDeleted }); await qc.invalidateQueries({ queryKey: ['feedback-reports', brandId ?? '', locationId ?? ''] }); },
        onError: () => toast({ severity: 'error', message: strings.feedbackReportDeleteError }),
    });

    const reportColumns: GridColDef<ReportConfiguration>[] = [
        { field: 'reportFrequency', headerName: strings.feedbackFrequency, flex: 1, valueGetter: (_value, row) => frequencyLabel(row.reportFrequency) },
        { field: 'reportEmailAddresses', headerName: strings.feedbackEmails, flex: 3 },
        { field: 'scope', headerName: strings.feedbackScope, flex: 1, valueGetter: (_value, row) => (row.locationId ? strings.feedbackLocation : strings.brand) },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<ReportConfiguration>) => [
                <DataGridEditButton id={params.id.toString()} onClick={() => { setEditingReport(params.row); setReportDialogOpen(true); }} aria-label="edit report" readOnly={!canUpdate} />,
                <DataGridDeleteButton id={params.id.toString()} warningMessage={strings.feedbackDeleteReportConfirm} onClick={(id) => deleteReport(id)} aria-label="delete report" hidden={!canDelete} />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.feedbackSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                    <Tab label={strings.feedbackAlerts} />
                    <Tab label={strings.reports} />
                </Tabs>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, p: 2, overflow: 'auto' }}>
                {tab === 0 ? (
                    !locationId ? (
                        <Typography variant="body2" color="text.secondary">{strings.feedbackSelectLocationForAlerts}</Typography>
                    ) : (
                        <Stack spacing={2} sx={{ maxWidth: 600 }}>
                            <TextField label={strings.feedbackTriggerEmails} value={emails} onChange={(e) => setEmails(e.target.value)} helperText={strings.feedbackTriggerEmailsHelp} fullWidth multiline minRows={2} />
                            <TextField label={strings.feedbackTriggerPhones} value={phones} onChange={(e) => setPhones(e.target.value)} helperText={strings.feedbackCommaSeparated} fullWidth multiline minRows={2} />
                            <Box>
                                {canUpdate && <Button variant="contained" loading={isSavingAlert} onClick={() => saveAlert()}>{strings.feedbackSaveAlerts}</Button>}
                            </Box>
                        </Stack>
                    )
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                                {locationId ? strings.feedbackReportsBothScope : strings.feedbackReportsBrandScope}
                            </Typography>
                            {canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingReport(null); setReportDialogOpen(true); }} size="input">{strings.feedbackNewReport}</Button>}
                        </Stack>
                        <Spacer y={2} />
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <DataGrid
                                rows={reports ?? []}
                                columns={reportColumns}
                                autoPageSize
                                loading={isReportsLoading}
                                localeText={{ noRowsLabel: strings.feedbackNoReports }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <ReportDialog
                open={reportDialogOpen}
                brandId={brandId}
                locationId={locationId}
                report={editingReport}
                readOnly={!!editingReport && !canUpdate}
                onClose={() => setReportDialogOpen(false)}
            />
        </Box>
    );
};

export default Configuration;
